/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { APIEnum } from '../../modules/loadbalancer/shared/enums/api';
import { ExternalUtilsService } from '../../pages/shared/external-utils-service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoginModel } from 'src/app/modules/loadbalancer/shared/models';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { PortalUrlsEnum } from '../../modules/centralisedManagement/shared/enums';
import { catchError, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * Available request options:
 * skip: boolean = Send the request without the queue.
 * attempts: number = Total number of times to attempt a request.
 * timeout: number = The amount of time before a request timesout.
 */

@Injectable()
export class AuthApiService {

    constructor(
        private http: HttpClient,
        private externalUtilsService: ExternalUtilsService
    ) {
    }

    /**
     * API call to login
     * @param user
     * @returns {Promise<any>}
     */
    public login(user: LoginModel): Observable<any> {
        return new Observable((observer) => {
            const headers = new Headers();
            headers.append('Content-Type', 'application/json');

            this.http.post(
                'https://' + user.applianceAddress + ':' +
                user.appliancePort + '/api/' +
                APIEnum.POST_VERSION + '/token',
                user
            )
                .pipe(
                    timeout(10000),
                    catchError(
                        err => {
                            return observableThrowError(err);
                        }
                    )
                )
                .subscribe({
                    next: (response: any): void => {
                        // Handle our response status code
                        switch (response.status) {
                            case 'error':
                            case 'fail':
                                observer.error(response.message);
                                break;
                            default:
                                break;
                        }

                        // Return with our response
                        observer.next(response.data);
                        observer.complete();
                    },
                    error: (error: any): void => {
                        observer.error(this.externalUtilsService.formatErrorMessage(error, user));
                        observer.complete();
                    }
                });
        });
    }

    /**
     *
     * @param email
     */
    public loginWithSaml(email: any): Observable<any> {
        return new Observable((observer) => {
            const headers = new Headers();
            headers.append('Content-Type', 'application/json');
            const url = environment.production
                ? '/api/v1/accounts/auth/saml'
                : PortalUrlsEnum.NON_PRODUCTION + '/api/v1/accounts/auth/saml';
            this.http.post(
                url,
                email
            )
                .pipe(
                    timeout(10000),
                    catchError(
                        err => {
                            return observableThrowError(err);
                        }
                    )
                )
                .subscribe({
                    next: (response: any): void => {
                        // Handle our response status code
                        switch (response.status) {
                            case 'error':
                            case 'fail':
                                break;
                            default:
                                break;
                        }

                        // Return with our response
                        observer.next(response.data);
                        observer.complete();
                    },
                    error: (error: any): void => {
                        observer.error(this.externalUtilsService.formatErrorMessage(error));
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Invalidate the JWT on logout.
     */
    public invalidateJWT(): Observable<any> {
        return new Observable((observer) => {
            const headers = new Headers();
            headers.append('Content-Type', 'application/json');
            const url = environment.production
                ? '/api/v1/accounts/logout'
                : PortalUrlsEnum.NON_PRODUCTION + '/api/v1/accounts/logout';

            this.http.delete(
                url,
            ).subscribe(
                {
                    next: (response: any): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: (error: any): void => {
                        observer.error(this.externalUtilsService.formatErrorMessage(error));
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Ping API
     * NOTE: the option withCredentials: false. This will
     * @param url
     * @param timeoutLimit
     */
    public ping(url: { applianceAddress: any; appliancePort: any; }, timeoutLimit = 10000): Observable<any> {
        return new Observable((observer) => {
            const urlString = 'https://' + url.applianceAddress + ':' +
                url.appliancePort + '/api/' +
                APIEnum.POST_VERSION + '/ping';

            this.http.get(urlString, { withCredentials: false })
                .pipe(
                    timeout(timeoutLimit),
                    catchError(
                        err => {
                            return observableThrowError(err);
                        }
                    )
                )
                .subscribe({
                    next: (response: any): void => {
                        // Handle our response status code
                        switch (response.status) {
                            case 'error':
                            case 'fail':
                                observer.error(response.message);
                                break;
                            default:
                                break;
                        }

                        // Return with our response
                        observer.next(response.data);
                        observer.complete();
                    },
                    error: (error: any): void => {
                        observer.error(this.externalUtilsService.formatErrorMessage(error, url));
                        observer.complete();
                    }
                });
        });
    }
}
